import { Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

interface AffectedSubscriber {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

interface AffectedData {
  count: number;
  subscribers: AffectedSubscriber[];
}

interface AffectedSubscribersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changeDescription: string;
  operationType: 'add' | 'remove' | 'update';
  affectedData: AffectedData | null;
  isLoading: boolean;
  onConfirm: (applyToExisting: boolean) => void;
}

function formatSubscriberName(subscriber: AffectedSubscriber): string {
  const parts: string[] = [];
  if (subscriber.firstName) {
    parts.push(subscriber.firstName);
  }
  if (subscriber.lastName) {
    parts.push(subscriber.lastName);
  }
  const fullName = parts.length > 0 ? parts.join(' ') : `User #${subscriber.userId}`;
  const username = subscriber.username ? ` @${subscriber.username}` : '';
  return `${fullName}${username}`;
}

export function AffectedSubscribersDialog({
  open,
  onOpenChange,
  changeDescription,
  operationType,
  affectedData,
  isLoading,
  onConfirm,
}: AffectedSubscribersDialogProps) {
  const count = affectedData?.count ?? 0;
  const subscribers = affectedData?.subscribers ?? [];
  const displayedSubscribers = subscribers.slice(0, 10);
  const remainingCount = count - displayedSubscribers.length;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Затронутые подписчики
          </AlertDialogTitle>
          <AlertDialogDescription>
            Это изменение затронет <strong>{count}</strong> активных подписчиков.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-sm font-medium">Что меняется:</p>
              <p className="text-sm text-muted-foreground mt-1">{changeDescription}</p>
            </div>

            {displayedSubscribers.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Подписчики:</p>
                <ul className="space-y-1">
                  {displayedSubscribers.map((subscriber) => (
                    <li key={subscriber.userId} className="text-sm text-muted-foreground pl-2">
                      {formatSubscriberName(subscriber)}
                    </li>
                  ))}
                </ul>
                {remainingCount > 0 && (
                  <p className="text-xs text-muted-foreground pl-2 pt-1">
                    ...и ещё {remainingCount}
                  </p>
                )}
              </div>
            )}

            {operationType === 'update' && (
              <div className="rounded-md border border-amber-500/20 bg-amber-500/10 p-3">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Выберите: применить изменения только к новым подпискам или также к существующим активным.
                </p>
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          {operationType === 'add' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onConfirm(false);
                  onOpenChange(false);
                }}
              >
                Только для новых
              </Button>
              <Button
                onClick={() => {
                  onConfirm(true);
                  onOpenChange(false);
                }}
              >
                Применить ко всем текущим
              </Button>
            </>
          )}
          {operationType === 'remove' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onConfirm(false);
                  onOpenChange(false);
                }}
              >
                Только для новых
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onConfirm(true);
                  onOpenChange(false);
                }}
              >
                Отозвать у текущих
              </Button>
            </>
          )}
          {operationType === 'update' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onConfirm(false);
                  onOpenChange(false);
                }}
              >
                Только для новых
              </Button>
              <Button
                onClick={() => {
                  onConfirm(true);
                  onOpenChange(false);
                }}
              >
                Применить ко всем текущим
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
